import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import GlobeMap3D from '@/components/three/GlobeMap3D';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Locate, LoaderCircle, Filter, X } from 'lucide-react';
import ListingCard from '@/components/ListingCard';
import { toast } from 'sonner';
import Mascot from '@/components/Mascot';

export default function MapPage() {
  const [items, setItems] = useState([]);
  const [center, setCenter] = useState([42.5, 12.5]);
  const [zoom, setZoom] = useState(6);
  const [myLoc, setMyLoc] = useState(null);
  const [locating, setLocating] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [f, setF] = useState({
    listing_type: '', price_min: '', price_max: '', area_min: '', area_max: '',
  });

  useEffect(() => {
    api.get('/listings', { params: { limit: 500 } }).then(r => setItems(r.data.items || []));
  }, []);

  const setFF = (k, v) => setF(p => ({ ...p, [k]: v }));
  const resetFilters = () => setF({ listing_type: '', price_min: '', price_max: '', area_min: '', area_max: '' });

  // Filtro client-side sui risultati già caricati
  const filtered = useMemo(() => {
    return items.filter(l => {
      if (f.listing_type && l.listing_type !== f.listing_type) return false;
      if (f.price_min && (l.price ?? 0) < Number(f.price_min)) return false;
      if (f.price_max && (l.price ?? Infinity) > Number(f.price_max)) return false;
      if (f.area_min && (l.area_hectares ?? 0) < Number(f.area_min)) return false;
      if (f.area_max && (l.area_hectares ?? Infinity) > Number(f.area_max)) return false;
      return true;
    });
  }, [items, f]);

  const activeCount = Object.values(f).filter(Boolean).length;

  const locate = () => {
    if (!('geolocation' in navigator)) return toast.error('Geolocalizzazione non supportata');
    if (window.isSecureContext === false) return toast.error('Geolocalizzazione richiede HTTPS');
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = [pos.coords.latitude, pos.coords.longitude];
        setCenter(c); setZoom(11); setMyLoc({ lat: c[0], lng: c[1] });
        setLocating(false);
        toast.success('Posizione trovata');
      },
      (err) => {
        setLocating(false);
        const msg = err.code === 1 ? 'Hai negato il permesso di geolocalizzazione.'
          : err.code === 2 ? 'Posizione non disponibile.'
          : err.code === 3 ? 'Timeout richiesta posizione.' : 'Errore geolocalizzazione.';
        toast.error(msg);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 pb-28">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-6">
        <div className="flex items-end gap-4">
          <Mascot pose="point" size={110} className="hidden md:block shrink-0 -mb-1" data-testid="map-hero-mascot"/>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta mb-3">Mappa</div>
            <h1 className="font-display text-4xl md:text-5xl tracking-tighter">Ogni terreno, geolocalizzato.</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {filtered.length} di {items.length} terreni visibili {activeCount>0 && <span className="text-terracotta">· {activeCount} filtri attivi</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="rounded-full h-11"
            onClick={()=>setShowFilters(s=>!s)}
            data-testid="map-toggle-filters"
          >
            <Filter size={14} className="mr-2"/>
            Filtri {activeCount>0 && <span className="ml-1.5 text-[10px] bg-chartreuse text-obsidian rounded-full px-1.5 font-bold">{activeCount}</span>}
          </Button>
          <Button
            data-testid="map-use-my-location"
            className="rounded-full h-11 px-5"
            onClick={locate}
            disabled={locating}
          >
            {locating
              ? <><LoaderCircle size={14} className="mr-2 animate-spin"/> Rilevamento…</>
              : <><Locate size={14} className="mr-2"/> Usa la mia posizione</>}
          </Button>
        </div>
      </div>

      {showFilters && (
        <div className="rounded-2xl border border-border bg-card p-5 mb-6" data-testid="map-filters-panel">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display text-lg">Filtra sulla mappa</div>
            {activeCount>0 && (
              <button onClick={resetFilters} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                <X size={11}/> Reset
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Operazione</div>
              <select
                value={f.listing_type}
                onChange={e=>setFF('listing_type', e.target.value)}
                data-testid="map-filter-type"
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm h-10"
              >
                <option value="">Tutte</option>
                <option value="sale">Vendita</option>
                <option value="rent">Affitto</option>
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Prezzo min €</div>
              <Input type="number" value={f.price_min} onChange={e=>setFF('price_min', e.target.value)} placeholder="0"/>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Prezzo max €</div>
              <Input type="number" value={f.price_max} onChange={e=>setFF('price_max', e.target.value)} placeholder="∞"/>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Sup. min (ha)</div>
              <Input type="number" step="0.1" value={f.area_min} onChange={e=>setFF('area_min', e.target.value)} placeholder="0"/>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Sup. max (ha)</div>
              <Input type="number" step="0.1" value={f.area_max} onChange={e=>setFF('area_max', e.target.value)} placeholder="∞"/>
            </div>
          </div>
        </div>
      )}

      <GlobeMap3D listings={filtered} height="78vh" focus={center} zoom={zoom} myLocation={myLoc}/>

      {filtered.length > 0 && (
        <div className="mt-10">
          <h2 className="font-display text-2xl mb-4">Terreni visibili sulla mappa</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.slice(0,12).map(l => <ListingCard key={l.listing_id} listing={l}/>)}
          </div>
        </div>
      )}
    </div>
  );
}
